/** Recapitulatif du risque dans quatre cartes. */
import DashboardNumber from "@/components/dashboard-number"
import UnitChip from "@/components/UnitChip"
import { OverviewCard, SelectedScaleItem, WizardOverviewField } from "@/components/WizardOverview"
import { useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { formatRiskValue, getRiskAttributeFields } from "@/entities/physical-risk/model/physical-risk.utils"
import { PhysicalRiskMeter } from "@/entities/physical-risk/ui/PhysicalRiskMeter"
import PreventionActionsChecklist from "@/entities/prevention-action/ui/PreventionActionsChecklist"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import populationStore from "@/entities/population/model/population.store"
import siteStore from "@/entities/site/model/site.store"
import PhysicalRiskPhotoPanel from "./PhysicalRiskPhotoPanel"
import { WizardStep } from "./physical-risk-wizard.constants"

interface Props {
  getStepLockReason?: (step: WizardStep) => string | undefined
  isReadonly?: boolean
  onReviewPrevention?: (suggestedPreventionValue: number) => void
  onStepClick?: (step: WizardStep) => void
}

export default function PhysicalRiskOverviewStep({ getStepLockReason, onReviewPrevention, onStepClick, isReadonly = false }: Props) {
  const draftRisk = useAppSelector(physicalRiskStore.selectors.draft)
  const sites = useAppSelector((state) => siteStore.selectors.listForRisk(state, draftRisk))
  const populations = useAppSelector((state) => populationStore.selectors.listForRisk(state, draftRisk))
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const preventionActions = useAppSelector((state) => preventionActionStore.selectors.listForRisk(state, draftRisk.id))
  const showCalculations = useAppSelector((state) => state._session.userOptions.risksShowCalculations)
  const isIdtreeScoreHidden = useAppSelector((state) => state._session.accountRisksSettings.isIdtreeScoreHidden)
  const attributes = getRiskAttributeFields(draftRisk)
  const primaryAttributes = attributes.slice(0, 2)
  const secondaryAttributes = attributes.slice(2)
  const hasNoSelectedUnit = sites.length === 0 && populations.length === 0

  return (
    <div className="flex flex-col gap-20">
      <div className="mt-20">
        <OverviewCard
          lockReason={getStepLockReason?.("DESCRIPTION")}
          title="Description"
          onClick={isReadonly ? undefined : () => onStepClick?.("DESCRIPTION")}>
          <div className="flex items-start gap-[40px]">
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-[40px]">
              <div className="flex min-w-0 flex-col gap-20">
                {primaryAttributes.map((attribute) => (
                  <WizardOverviewField
                    key={attribute.id}
                    label={attribute.name}
                    value={attribute.value || "Non renseigné"}
                  />
                ))}
              </div>
              <div className="flex min-w-0 flex-col gap-20">
                {secondaryAttributes.map((attribute) => (
                  <WizardOverviewField
                    key={attribute.id}
                    label={attribute.name}
                    value={attribute.value || "Non renseigné"}
                  />
                ))}
                <WizardOverviewField
                  label="Famille de risque"
                  value={draftRisk.cFamily?.name || "Non renseignée"}
                />
              </div>
            </div>
            {draftRisk.photoId && (
              <div className="w-[162px] shrink-0">
                <PhysicalRiskPhotoPanel isReadonly isTopAligned />
              </div>
            )}
          </div>
        </OverviewCard>
      </div>

      <OverviewCard title="Périmètre" onClick={isReadonly ? undefined : () => onStepClick?.("PERIMETER")}>
        <div className="flex min-w-0 flex-wrap gap-[10px]">
          {hasNoSelectedUnit && activeEstablishment?.name && (
            <UnitChip
              unit={{ type: "establishment", name: activeEstablishment.name }}
              onClick={isReadonly ? undefined : () => onStepClick?.("PERIMETER")}
            />
          )}
          {sites.map((site) => (
            <UnitChip key={site.id} unit={{ type: "site", site }} onClick={isReadonly ? undefined : () => onStepClick?.("PERIMETER")} />
          ))}
          {populations.map((population) => (
            <UnitChip
              key={population.id}
              unit={{ type: "population", population }}
              onClick={isReadonly ? undefined : () => onStepClick?.("PERIMETER")}
            />
          ))}
        </div>
      </OverviewCard>

      <div className="flex flex-col gap-20">
        <OverviewCard
          lockReason={getStepLockReason?.("SEVERITY")}
          title="Gravité"
          onClick={isReadonly ? undefined : () => onStepClick?.("SEVERITY")}>
          <SelectedScaleItem scale={draftRisk.cSeverityScale} value={draftRisk.severity} />
        </OverviewCard>

        <OverviewCard
          lockReason={getStepLockReason?.("FREQUENCY")}
          title="Fréquence"
          onClick={isReadonly ? undefined : () => onStepClick?.("FREQUENCY")}>
          <SelectedScaleItem scale={draftRisk.cFrequencyScale} value={draftRisk.frequency} />
        </OverviewCard>

        <OverviewCard
          lockReason={getStepLockReason?.("EVALUATION")}
          title="Évaluation"
          onClick={isReadonly ? undefined : () => onStepClick?.("EVALUATION")}>
          <div className="flex items-start gap-20">
            <div className="min-w-0 flex-1">
              <SelectedScaleItem isPrevention scale={draftRisk.cPreventionScale} value={draftRisk.prevention} />
            </div>
            <div className="my-auto shrink-0">
              <div className="flex items-center gap-20">
                <PhysicalRiskMeter risk={draftRisk} size="lg" />
                {!isIdtreeScoreHidden && (
                  <div className="shrink-0">
                    <DashboardNumber
                      color={draftRisk.cValueEffectiveColor ?? undefined}
                      count={draftRisk.cValueEffectiveLabel}
                      header="Niveau de risque"
                      fontSize={18}
                      isWithoutMargin
                    />
                  </div>
                )}
                <div className="flex-1" />
              </div>
            </div>
          </div>
          {showCalculations && (
            <div className="grey-t mt-20">
              Détail du calcul : {draftRisk.severity} × {draftRisk.frequency} / {draftRisk.prevention} ={" "}
              <b style={{ color: draftRisk.cValueEffectiveColor ?? undefined }}>
                {formatRiskValue(draftRisk.cValueEffective ?? 0)}
              </b>
            </div>
          )}
        </OverviewCard>
      </div>

      <OverviewCard
        lockReason={getStepLockReason?.("PREVENTION")}
        title="Prévention"
        onClick={isReadonly ? undefined : () => onStepClick?.("PREVENTION")}>
        <div onClick={(event) => event.stopPropagation()}>
          <PreventionActionsChecklist isReadonly={isReadonly} preventionActions={preventionActions} onReviewPrevention={onReviewPrevention} />
        </div>
      </OverviewCard>
    </div>
  )
}
